/**
 * A panel icon that calls for attention (2026-10-08: "don't automatically
 * open the depot when standing next to it. show the panelicon for it with
 * an urgent appearance of some kind so it's obvious").
 *
 * Arriving beside a depot box shows the Depot icon marked urgent — style.css
 * gives .panelicon.urgent an accent ring that pulses, or holds steady for
 * whoever asks for less motion, and a dot in its corner — and leaves the
 * panel shut. Opening the panel, by its icon or anything else, calms the
 * icon while it is open, and shutting it again beside the box marks it
 * urgent again (2026-10-19: "always highlight the depot button when
 * standing in range, even after the dialog is closed"); until then it
 * stayed calm, the shutting taken for a choice already made. Stepping away
 * hides the icon and shuts the panel.
 *
 * Kept apart from main.ts, on the least of an element it needs, so the
 * rules can be run under node without a page.
 */
export const URGENT = "urgent";

/** What of an element these rules touch: an icon or a panel. */
export interface Marked {
  hidden: boolean;
  classList: { toggle(token: string, force?: boolean): boolean; contains(token: string): boolean };
  setAttribute(name: string, value: string): void;
  removeAttribute(name: string): void;
}

/** What a screen reader hears besides the icon's name while it is urgent. */
export const URGENT_SAYS = "Beside you now: press to open";

/** Mark an icon urgent, or calm it. */
export function markUrgent(icon: Marked, urgent: boolean) {
  icon.classList.toggle(URGENT, urgent);
  if (urgent) icon.setAttribute("aria-description", URGENT_SAYS);
  else icon.removeAttribute("aria-description");
}

/**
 * Light an icon for its panel being open or not. An open panel's icon is
 * calm: what it was calling for has been seen. A shut one calls again if
 * its panel belongs to a place you are at (calls), as the Depot's does.
 */
export function markOpen(icon: Marked, open: boolean, calls = false) {
  icon.classList.toggle("on", open);
  icon.setAttribute("aria-pressed", String(open));
  if (open) markUrgent(icon, false);
  else if (calls) markUrgent(icon, true);
}

/**
 * The icon of a panel that belongs to a place, after the panel has been
 * shown or hidden by arriving or leaving: it comes and goes with the panel,
 * urgent on arriving and calm on leaving. Returns whether the panel is to
 * be open afterwards, which is never — arriving does not open it, and
 * leaving shuts it.
 */
export function placeIconFollows(panel: Marked, icon: Marked): boolean {
  icon.hidden = panel.hidden;
  markUrgent(icon, !panel.hidden);
  return false;
}
